import { hexColorZodSchema } from "@@/shared/stylable/hex-color/hex-color";
import type { Except } from "type-fest";
import { z } from "zod";
import { isoAndUnixZodSchema, nowIsoAndUnix } from "../date-time/iso-and-unix";
import { entityStateZodSchema } from "../entity-status";
import { metaContentZodSchema } from "../meta-content/meta-content";
import { providerIdZodSchema } from "../providers/provider-id";
import {
    languageCodeMap,
    languageCodeZodSchema,
} from "../translations/language-code";
import { userIdZodSchema } from "../users/user-id";
import { pageBlockZodSchema } from "./page-blocks/page-block";
import { pageBlockIdZodSchema } from "./page-blocks/page-block-id";
import type { PageId} from "./page-id";
import { pageIdFactory, pageIdZodSchema } from "./page-id";
import { pageSlugZodSchema } from "./page-slug";
import { pageStateZodSchema } from "./page-state";

const pageZodSchema = z.object({
    _id: pageIdZodSchema,
    _type: z.literal("PAGE"),
    _createdAt: isoAndUnixZodSchema,
    _createdBy: userIdZodSchema,
    _editedAt: isoAndUnixZodSchema,
    _editedBy: userIdZodSchema,
    _status: entityStateZodSchema,
    state: pageStateZodSchema,
    slug: pageSlugZodSchema,
    meta: metaContentZodSchema,
    blocks: z.record(pageBlockIdZodSchema, pageBlockZodSchema),
    blockSortOrder: z.array(pageBlockIdZodSchema),
    ownerId: providerIdZodSchema.optional(),
    backgroundColor: hexColorZodSchema.optional(),
    pageInnerWidth: z.number().optional(),
    pageMaxWidth: z.number().optional(),
    fallbackPageId: pageIdZodSchema.nullish(),
    /**
     * Translations available for the page for visitors to
     * pick between when visiting and to be available to edit for page editors
     * @type {LanguageCode[]}
     */
    languages: z.array(languageCodeZodSchema).default([languageCodeMap.sv]),
    branding: z
        .object({
            borderRadius: z
                .object({
                    topLeft: z.number().optional(),
                    topRight: z.number().optional(),
                    bottomLeft: z.number().optional(),
                    bottomRight: z.number().optional(),
                })
                .optional(),
        })
        .optional(),
});

type Page = z.infer<typeof pageZodSchema>;

type PageFactoryParams = Except<
    Page,
    "_id" | "_createdAt" | "_type" | "_status" | "_editedAt" | "_editedBy"
> & {
    _id?: PageId;
};

const pageFactory = (params: PageFactoryParams): Page => {
    const now = nowIsoAndUnix();
    return {
        ...params,
        _id: params._id ?? pageIdFactory(),
        _createdAt: now,
        _createdBy: params._createdBy,
        _editedBy: params._createdBy,
        _editedAt: now,
        _status: "ACTIVE",
        _type: "PAGE",
        pageMaxWidth: params.pageMaxWidth ?? 1200,
        pageInnerWidth: params.pageInnerWidth ?? 800,
        languages: params.languages.includes(languageCodeMap.sv)
            ? params.languages
            : [languageCodeMap.sv, ...params.languages],
        branding: params.branding,
    };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isPage = (content: any): content is Page => {
    if (!content) return false;
    return "_type" in content && content._type === "PAGE";
};

export { isPage, pageFactory, pageZodSchema };
export type { Page };
